import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Popover from "@mui/material/Popover";
import BinaryStateButton from "components/BinaryStateButton";
import { BreadcrumbProps, Breadcrumbs } from "components/Breadcrumbs/Breadcrumbs";
import { Icon } from "components/Icon/Icon";
import { IconCollection } from "components/Icon/IconCollection";
import PageHeader from "components/Layout/PageHeader";
import { UnsavedChangesModal } from "components/Prompt/UnsavedChangesModal";
import { singleCustomObjectProvisioningStateSelector } from "components/ProvisioningStateDot/ProvisioningState.selectors";
import { ProvisioningStateData } from "components/ProvisioningStateDot/ProvisioningStateDot";
import RecordLabel from "components/RecordLabel";
import RoleSelect from "components/RoleSelect";
import StatusBadge from "components/StatusBadge";
import { DocsInfoTooltip } from "components/Tooltip/DocsInfoTooltip";
import OverflowTooltip from "components/Tooltip/OverflowTooltip";
import Tooltip from "components/Tooltip/Tooltip";
import { resourceByName } from "config/resources";
import { ActivityConfig, FeConfigType, RelationshipConfig, ResourceConfig } from "config/config";
import { getIsReadonly } from "config/resourceUtils";
import { getCustomObjectProvisioningStateColors } from "config/ts-utils";
import { ResourcePageHeaderIconForm } from "containers/Resources/components/ResourcePageHeaderIconForm/ResourcePageHeaderIconForm";
import { ResourcePageHeaderDescriptionForm } from "containers/Resources/components/ResourcesPageHeaderDescriptionForm/ResourcesPageHeaderDescriptionForm";
import { ResourcePageHeaderTitleForm } from "containers/Resources/components/ResourcesPageHeaderTitleForm/ResourcesPageHeaderTitleForm";
import { GitAction } from "containers/Resources/screens/ResourcesSingle/components/GitAction/GitAction";
import {
    isContainerType,
    UncommittedDataGroupedByName,
} from "containers/Resources/screens/ResourcesSingle/components/GitAction/commitActions/utils";
import ResourceStats from "containers/Resources/screens/ResourcesSingle/components/ResourceStats";
import { DetailsPopover } from "containers/Resources/screens/ResourcesSingle/components/ResourcesSinglePageHeader/DetailsPopover";
import { HiddenActionsComponent } from "containers/Resources/screens/ResourcesSingle/components/ResourcesSinglePageHeader/HiddenActionsComponent";
import { PinPopover } from "containers/Resources/screens/ResourcesSingle/components/ResourcesSinglePageHeader/PinPopover";
import {
    getBootstrapUserBadge,
    getBundleContentBadge,
    getDeletedBadges,
    getDeprecationBadge,
    getPendingDeleteBadge,
    getPendingRestoreBadge,
    getPendingTrashBadge,
    getReadonlyBadges,
    getRecordContainer,
    getSyntaxErrorBadge,
} from "containers/Resources/screens/ResourcesSingle/utils";
import { checkBootstrapUser } from "containers/Workspaces/utils";
import { isEmpty } from "lodash";
import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { useHistory } from "react-router";
import { customObjectCanvasPathFragment } from "utils/RouterManager/constants";
import { mapScreensToPaths } from "utils/RouterManager/utils";
import { getCanvasLink, getRelativeUrl } from "utils/service";
import { useSticky } from "utils/useSticky";
import { getLeadingSpaces, getTrailingSpaces } from "utils/utils";
import { AccessList } from "../AccessList/AccessList";
import "./ResourcesSinglePageHeader.scss";

const recordDocLinks = {
    connector: "connection-resource",
    custom_object: "object-templates-custom-objects#custom-objects",
    object_template: "object-templates-custom-objects#object-templates",
    schedule: "scheduling#schedule",
    scheduler: "scheduling#scheduler",
    setting: "settings-and-locks",
    schema: "schema-creation-and-validation#schema-resource",
    bundle_repository: "bundles#bundle-repository",
    ldap_config: "ldap-integration",
    role: "rbac",
    sync_config: "workspace-linking",
    devolutions_config: "vault-integration#devolutions-server",
    vault_config: "vault-integration#hashicorp-vault",
    flow: "flows",
    file: "files",
    project: "projects",
    bundle: "bundles",
    webhook: "webhooks",
    wrapper: "wrappers",
    plugin: "plugins",
};

interface ResourceSinglePageHeaderProps {
    editIcon?: boolean;
    /** Enable the curated built-in-icon picker alongside upload (opt-in per resource). */
    curatedIcons?: boolean;
    editName?: boolean;
    showDescription?: boolean;
    editDescription?: boolean;
    handleSubmit: () => Promise<any>;
    pristine: boolean;
    resourceData?: any;
    resourceName: FeConfigType;
    fetchResourceData: () => void;
    /**
     * Optional manual-refresh handler for the header Refresh button. When provided
     * (static single-record screens), renders a Refresh IconButton that re-fetches
     * the record and shows current server state. Omitted for live screens (e.g.
     * execution) and new-record create screens. See
     * operator-task-fe-single-record-screen-refresh-button.
     */
    onRefresh?: () => void;
    isRefreshing?: boolean;
    resetForm: () => void;
    backButtonLink?: any;
    resourceConfig: ResourceConfig | ActivityConfig | RelationshipConfig;
    defaultValues?: any;
    checkIfNameExists?: (e) => void;
    titleIcon?: string;
    isDisabled?: boolean;
    fetchSearchTabs?: () => Promise<any>;
    isNewRecord?: boolean;
    newRecordLocationData?: {
        id: string;
        type: "project" | "bundle" | "workspace";
        name: string;
        icon: any;
    };
    uncommittedDataByPath?: UncommittedDataGroupedByName;
    hasValidationErrors?: boolean;
    isGitLocked?: boolean;
    isFetching?: boolean;
    isGitEnabled?: boolean;
    bootstrapUserName?: string;
    iri?: string;
}

const ResourceSinglePageHeader = ({
    editIcon = false,
    curatedIcons = false,
    editName = true,
    showDescription = true,
    editDescription = true,
    children,
    handleSubmit,
    pristine,
    resourceData,
    resourceName,
    fetchResourceData,
    onRefresh,
    isRefreshing,
    resetForm,
    backButtonLink,
    resourceConfig,
    defaultValues,
    checkIfNameExists,
    titleIcon,
    isDisabled,
    fetchSearchTabs,
    isNewRecord,
    newRecordLocationData,
    uncommittedDataByPath = {},
    hasValidationErrors,
    isGitLocked,
    isFetching,
    isGitEnabled,
    bootstrapUserName,
    iri,
}: React.PropsWithChildren<ResourceSinglePageHeaderProps>) => {
    const history = useHistory();

    const [statsPopoverEl, setStatsPopoverEl] = useState(null);
    const [accessListPopoverEl, setAccessListPopoverEl] = useState(null);

    const [isModalVisible, setModalVisible] = useState(false);

    const applyAction = useRef<() => void>();

    const projectOrBundleId = resourceData?.project_id ?? resourceData?.bundle_id;
    const recordId = resourceData?.id;

    const isReadOnly = getIsReadonly(resourceConfig, resourceData);

    const pageHeaderIcon = (
        <ResourcePageHeaderIconForm
            handleSubmit={handleSubmit}
            recordId={recordId}
            isReadOnly={isReadOnly}
            enableCuratedPicker={curatedIcons}
        />
    );

    const canEditTitle = editName && !isReadOnly && !resourceData?.deleted_at;

    const pageHeaderTitle = canEditTitle ? (
        <ResourcePageHeaderTitleForm
            defaultValue={defaultValues?.["name"]}
            checkIfNameExists={checkIfNameExists}
            isFetching={isFetching}
        />
    ) : (
        <OverflowTooltip overlay={resourceData?.name} isOverlapping={false}>
            <span className="text-xl text-bold">
                <span className="space-background">{getLeadingSpaces(resourceData?.name)}</span>
                {resourceData?.name?.trim() ?? ""}
                <span className="space-background">{getTrailingSpaces(resourceData?.name)}</span>
            </span>
        </OverflowTooltip>
    );

    const getPageHeaderSubtitle = () => {
        if (showDescription) {
            return (
                <ResourcePageHeaderDescriptionForm
                    defaultValue={defaultValues?.["description"]}
                    isReadOnly={!editDescription || isReadOnly || resourceData?.deleted_at}
                />
            );
        }
        return null;
    };
    const pageHeaderSubtitle = getPageHeaderSubtitle();

    const { ref, isSticky } = useSticky<HTMLDivElement>();
    const canvasType =
        resourceName === "custom_object" ? customObjectCanvasPathFragment : undefined;
    const canvasLink =
        resourceName === "object_template" || resourceName === "custom_object"
            ? getCanvasLink(
                  resourceData?.project_id ? "project" : "bundle",
                  projectOrBundleId,
                  canvasType,
                  "id",
                  // Deep-link the opened record as the canvas focus target so the
                  // canvas highlights + centers on it (opened from this record).
                  recordId
              )
            : getCanvasLink(resourceName, recordId, canvasType);

    // Whether the content is tall enough that the header should stick on scroll.
    // Reading form/header scrollHeight forces a synchronous layout reflow, so we
    // do it in an effect (on mount, resize, data change, and whenever the form
    // content actually resizes) instead of on every render.
    const [isScrollNeeded, setIsScrollNeeded] = useState(false);

    useLayoutEffect(() => {
        const computeScrollNeeded = () => {
            const form = document?.getElementsByTagName("form")?.[0];
            const header = document?.getElementsByClassName("page-header--form")?.[0];
            if (!form) {
                setIsScrollNeeded(false);
                return;
            }
            const headerScrollHeight = header?.scrollHeight ?? 0;
            // 80 - topbar height
            // 109 - sticky header min height
            const scrollHeightWithStickyHeader =
                80 + form.scrollHeight - headerScrollHeight + 109;
            setIsScrollNeeded(scrollHeightWithStickyHeader > window.innerHeight);
        };

        computeScrollNeeded();
        window.addEventListener("resize", computeScrollNeeded);

        const form = document?.getElementsByTagName("form")?.[0];
        let resizeObserver: ResizeObserver | undefined;
        if (form && typeof ResizeObserver !== "undefined") {
            resizeObserver = new ResizeObserver(computeScrollNeeded);
            resizeObserver.observe(form);
        }

        return () => {
            window.removeEventListener("resize", computeScrollNeeded);
            resizeObserver?.disconnect();
        };
    }, [resourceData, isSticky]);

    // Scroll-linked description collapse.
    // Margot (operator-task-fe-description-sticky-collapse-scroll-out-no-animation):
    // the description must leave — and return on scroll-up — exactly in step with
    // the scroll position, not via a fixed-duration CSS transition that lags a fast
    // scroll and reads as a "jump". We drive the subtitle's occupied height straight
    // from the scroll container's `scrollTop`, so the description recedes 1:1 with
    // the scroll (N px scrolled -> N px collapsed) until it is fully gone, and grows
    // back symmetrically on scroll-up. No time-based animation. The subtitle carries
    // a 12px top margin (PageHeader.scss), so both the grid-row height and that
    // margin are collapsed proportionally, keeping the total occupied height exactly
    // `naturalHeight + 12 - scrollTop`. See ui-design-principle-sticky-headers.
    useLayoutEffect(() => {
        // `.main__content` is the page scroll container (see Main.scss); `.main`
        // itself does not scroll, so its scrollTop stays 0.
        const scrollContainer = document.querySelector<HTMLElement>(
            ".page-container > .main > .main__content"
        );
        if (!scrollContainer) {
            return;
        }

        const SUBTITLE_MARGIN_TOP = 12; // px — PageHeader.scss .page-header__subtitle
        let naturalHeight = 0;
        // Last collapse fraction actually written to the DOM. Used to skip
        // redundant style writes when a scroll event resolves to a
        // visually-identical collapse (defense-in-depth against the
        // scroll-anchoring feedback loop the `overflow-anchor: none` on
        // `.main__content` breaks at its source — see
        // operator-task-fe-record-header-flap-on-partial-comment-scroll). A
        // sub-pixel change in the collapsed height is imperceptible, so
        // suppressing it costs nothing and avoids re-entrancy on any browser
        // that still nudges scrollTop on an above-the-fold layout write.
        let lastAppliedP: number | null = null;

        const getSubtitleEl = () =>
            document.querySelector<HTMLElement>(
                ".page-header--form .page-header__subtitle"
            );

        const clearCollapse = (el: HTMLElement) => {
            el.style.gridTemplateRows = "";
            el.style.marginTop = "";
            el.style.overflow = "";
            el.style.pointerEvents = "";
        };

        // Content (grid-row) height when fully expanded. Valid only when the inline
        // row constraint has been cleared, so every caller clears first.
        const measureNatural = (el: HTMLElement) => {
            naturalHeight = el.scrollHeight;
        };

        const applyCollapse = () => {
            const el = getSubtitleEl();
            if (!el) {
                return;
            }
            const s = scrollContainer.scrollTop;
            // Only collapse when the record is tall enough to scroll; at rest
            // (scrollTop <= 0) keep it fully expanded and (re)capture the natural
            // height for the next collapse.
            if (!isScrollNeeded || s <= 0) {
                clearCollapse(el);
                measureNatural(el);
                lastAppliedP = null;
                return;
            }
            if (naturalHeight <= 0) {
                // First collapse before we ever measured at rest: measure now.
                clearCollapse(el);
                measureNatural(el);
            }
            const total = naturalHeight + SUBTITLE_MARGIN_TOP;
            const p = Math.min(1, Math.max(0, s / total));
            // Skip the write when it resolves to a visually-identical collapse
            // (<0.5px change in the collapsed subtitle height). Never skip the
            // terminal fully-collapsed state (p === 1) so the subtitle always
            // ends exactly hidden.
            if (
                lastAppliedP !== null &&
                p !== 1 &&
                naturalHeight * Math.abs(p - lastAppliedP) < 0.5
            ) {
                return;
            }
            el.style.gridTemplateRows = `${naturalHeight * (1 - p)}px`;
            el.style.marginTop = `${SUBTITLE_MARGIN_TOP * (1 - p)}px`;
            el.style.overflow = "hidden";
            el.style.pointerEvents = p > 0 ? "none" : "";
            lastAppliedP = p;
        };

        const el0 = getSubtitleEl();
        if (el0) {
            clearCollapse(el0);
            measureNatural(el0);
        }
        applyCollapse();

        // Re-measure the natural height when the description content resizes (e.g.
        // "show more" expand, or entering/leaving edit) — but only while at rest,
        // where no inline row constraint is set, so this cannot loop with the inline
        // styles applyCollapse writes during a collapse.
        let contentResizeObserver: ResizeObserver | undefined;
        if (el0 && typeof ResizeObserver !== "undefined") {
            contentResizeObserver = new ResizeObserver(() => {
                if (scrollContainer.scrollTop <= 0) {
                    const el = getSubtitleEl();
                    if (el) {
                        clearCollapse(el);
                        measureNatural(el);
                    }
                }
            });
            contentResizeObserver.observe(el0);
        }

        scrollContainer.addEventListener("scroll", applyCollapse, { passive: true });
        window.addEventListener("resize", applyCollapse);

        return () => {
            scrollContainer.removeEventListener("scroll", applyCollapse);
            window.removeEventListener("resize", applyCollapse);
            contentResizeObserver?.disconnect();
            const el = getSubtitleEl();
            if (el) {
                clearCollapse(el);
            }
        };
    }, [resourceData, isScrollNeeded, editDescription, showDescription]);

    const provisioningStateSelector = useMemo(() => {
        if (resourceName === "custom_object") {
            return singleCustomObjectProvisioningStateSelector(recordId);
        }
        return () => null;
    }, [resourceName, recordId]);

    const provisioningState: ProvisioningStateData | undefined = useSelector(
        provisioningStateSelector,
        shallowEqual
    );

    // A custom object should ALWAYS show a badge with its current provisioning
    // state. The live websocket map (provisioningState) only carries COs that are
    // actively provisioning, so a READY / DELETED / *_FAILED record would show no
    // badge at all — fall back to the value loaded onto the record itself. The
    // live map still takes precedence so the badge updates in real time.
    const provisioningStateValue =
        resourceName === "custom_object"
            ? provisioningState?.provisioning_state ?? resourceData?.provisioning_state
            : undefined;

    const isBootstrapUser = checkBootstrapUser({
        name: resourceData?.name,
        bootstrapUserName,
        recordType: resourceData?.record_type,
    });

    const pageHeaderAdditionalActions = isNewRecord ? null : (
        <>
            {resourceData?.is_dirty && <StatusBadge type="primary-green" text="CHANGED" />}
            {resourceData?.is_submitted && <StatusBadge type="hover-grey" text="SUBMITTED" />}
            {provisioningStateValue && (
                <StatusBadge
                    type={getCustomObjectProvisioningStateColors(provisioningStateValue)}
                    text={provisioningStateValue}
                />
            )}
            {getReadonlyBadges({ resourceData, resourceConfig })}
            {getDeletedBadges({ resourceData })}
            {getPendingTrashBadge({ resourceData })}
            {getPendingRestoreBadge({ resourceData })}
            {getPendingDeleteBadge({ resourceData })}
            {getDeprecationBadge({ resourceData })}
            {getSyntaxErrorBadge({ resourceData })}
            {getBundleContentBadge({ resourceData })}
            {getBootstrapUserBadge({ isBootstrapUser })}
            {resourceData &&
                (resourceName === "project" ||
                    resourceName === "bundle" ||
                    resourceName === "workspace") && (
                    <GitAction
                        data={resourceData}
                        isDisabled={isEmpty(resourceData) || isDisabled}
                        afterAction={fetchResourceData}
                        fetchSearchTabs={() => fetchSearchTabs?.()}
                        uncommittedData={uncommittedDataByPath}
                        isGitLocked={isGitLocked}
                    />
                )}
            {resourceData?.identity_role_plain_list_identity_id && (
                <RoleSelect
                    identity={resourceData}
                    onSelect={fetchResourceData}
                    onDeselect={fetchResourceData}
                    onUpdate={fetchResourceData}
                />
            )}
            {!resourceConfig?.actions?.hidePin && <PinPopover resourceData={resourceData} />}
            {resourceConfig?.statsSidebar && resourceData && (
                <Tooltip overlay="Latest Executions">
                    <BinaryStateButton
                        trueStateIcon={IconCollection.chartDonut}
                        falseStateIcon={IconCollection.chartDonut}
                        trueStateColor="primary"
                        state={Boolean(statsPopoverEl)}
                        onClick={(event) => {
                            setStatsPopoverEl(event.target);
                        }}
                        className="statsButton"
                        disabled={isEmpty(resourceData)}
                    />
                    <Popover
                        open={Boolean(statsPopoverEl)}
                        anchorEl={statsPopoverEl}
                        onClose={() => {
                            setStatsPopoverEl(null);
                        }}
                        anchorOrigin={{
                            vertical: "bottom",
                            horizontal: "right",
                        }}
                        transformOrigin={{
                            vertical: "top",
                            horizontal: "right",
                        }}
                    >
                        <Box sx={{ p: 4 }}>
                            <ResourceStats
                                resourceData={resourceData}
                                resourceName={resourceName}
                            />
                        </Box>
                    </Popover>
                </Tooltip>
            )}
            {resourceName === "project" ||
            resourceName === "bundle" ||
            resourceName === "object_template" ||
            resourceName === "custom_object" ? (
                <Tooltip overlay={canvasType ? "Custom object canvas" : "Object template canvas"}>
                    <IconButton
                        disabled={isEmpty(resourceData)}
                        onClick={() => history.push(canvasLink)}
                    >
                        <Icon icon={IconCollection.vectorPolyLine} />
                    </IconButton>
                </Tooltip>
            ) : null}
            {(resourceName === "project" ||
                resourceName === "bundle" ||
                resourceName === "workspace") && (
                <Tooltip overlay="Access list">
                    <BinaryStateButton
                        trueStateIcon={IconCollection.security}
                        falseStateIcon={IconCollection.security}
                        trueStateColor="primary"
                        state={Boolean(accessListPopoverEl)}
                        onClick={(event) => {
                            setAccessListPopoverEl(event.target);
                        }}
                        className="accessListButton"
                        disabled={isEmpty(resourceData)}
                    />
                    <Popover
                        open={Boolean(accessListPopoverEl)}
                        anchorEl={accessListPopoverEl}
                        onClose={() => {
                            setAccessListPopoverEl(null);
                        }}
                        anchorOrigin={{
                            vertical: "bottom",
                            horizontal: "right",
                        }}
                        transformOrigin={{
                            vertical: "top",
                            horizontal: "right",
                        }}
                        className="resourceSinglePageHeaderPopover"
                    >
                        <Box sx={{ p: 4 }}>
                            <AccessList />
                        </Box>
                    </Popover>
                </Tooltip>
            )}
            {onRefresh && (
                <Tooltip overlay="Refresh — re-fetch this record from the server">
                    <IconButton
                        className={isRefreshing ? "refreshButton refreshButton--spinning" : "refreshButton"}
                        disabled={isEmpty(resourceData) || isRefreshing}
                        onClick={() => onRefresh()}
                        aria-label="Refresh record"
                    >
                        <Icon icon={IconCollection.refresh} />
                    </IconButton>
                </Tooltip>
            )}
            <DetailsPopover
                resourceName={resourceName}
                resourceData={resourceData}
                isFetching={isFetching}
                isDisabled={isDisabled}
                resourceConfig={resourceConfig}
                isGitEnabled={isGitEnabled}
                iri={iri}
                fetchResourceData={fetchResourceData}
            />
            <HiddenActionsComponent
                pristine={pristine}
                resourceData={resourceData}
                resourceName={resourceName}
                resourceConfig={resourceConfig}
                fetchResourceData={fetchResourceData}
                setModalVisible={setModalVisible}
                applyAction={applyAction}
                hasTooltip
                afterAction={fetchResourceData}
                uncommittedDataByPath={uncommittedDataByPath}
                provisioningState={provisioningState}
                iri={iri}
            />
        </>
    );

    const getBreadcrumbs = (): BreadcrumbProps[] => {
        let containerBreadcrumbs: BreadcrumbProps[] = [];

        const getLabel = () => {
            const connectorType =
                resourceData?.connector_type ?? defaultValues?.connector_type ?? "";
            const connectorId = resourceData?.connector_id_connector;

            return (
                <div className="flex items-center gap-8">
                    <div>{`${resourceName} ${resourceData?.type || ""} ${connectorType}`}</div>
                    {connectorId && (
                        <RecordLabel
                            link={getRelativeUrl("connector", connectorId.id)}
                            name={connectorId.name}
                            icon={resourceByName.connector.icon}
                        />
                    )}
                </div>
            );
        };

        if (isContainerType(resourceName)) {
            containerBreadcrumbs = [];
        } else if (!isEmpty(resourceData)) {
            if (resourceData.deleted_at) {
                containerBreadcrumbs = [
                    {
                        label: <RecordLabel name="Trash" icon={IconCollection.deleteOutline} />,
                        link: mapScreensToPaths.trash,
                    },
                ];
            } else {
                const { containerType: locationType, containerData: recordLocation } =
                    getRecordContainer(resourceData);

                containerBreadcrumbs =
                    locationType && recordLocation
                        ? [
                              {
                                  label: (
                                      <RecordLabel
                                          name={recordLocation.name}
                                          image={recordLocation.icon}
                                          iconByType={locationType}
                                      />
                                  ),
                                  link: getRelativeUrl(locationType, recordLocation.id),
                              },
                          ]
                        : [];
            }
        }

        return [
            ...containerBreadcrumbs,
            {
                link: "",
                label: getLabel(),
            },
        ];
    };

    const NewRecordHeadline = () => {
        const headline = `create new ${resourceName}`.toUpperCase();
        return (
            <div className="newRecordHeadline">
                <span>{headline}</span>
                <DocsInfoTooltip
                    title="Read more"
                    urlPath={`/docs/${recordDocLinks[resourceName]}`}
                />
            </div>
        );
    };

    const pageHeaderProps = {
        additionalActions: pageHeaderAdditionalActions,
        title: pageHeaderTitle,
        headline: isNewRecord ? (
            <NewRecordHeadline />
        ) : (
            <Breadcrumbs breadcrumbs={getBreadcrumbs()} />
        ),
        titleImage: editIcon ? pageHeaderIcon : "",
        titleIcon: editIcon ? null : titleIcon ?? (resourceConfig?.icon || IconCollection.codeTags),
        subtitle: editDescription ? pageHeaderSubtitle : "",
        // Pin the header from the very first pixel of scroll (`isScrollNeeded`,
        // known synchronously) so no header element ever travels upward and snaps
        // back. The collapsed-state affordances (divider) are gated separately on
        // the actually-scrolled signal (`isSticky`) so they never show at rest.
        // See operator-task-fe-description-sticky-collapse-scroll-out-no-animation.
        className: `page-header--form ${canEditTitle ? "" : "page-header--form__title-readonly"}  ${
            isScrollNeeded ? "page-header-sticky" : ""
        }${isSticky && isScrollNeeded ? " page-header--scrolled" : ""}`,
        backButtonLink,
    };

    return (
        <>
            {applyAction.current && (
                <UnsavedChangesModal
                    applyAction={applyAction.current}
                    saveContent={hasValidationErrors ? undefined : handleSubmit}
                    setModalVisible={setModalVisible}
                    isModalVisible={isModalVisible}
                    resetForm={resetForm}
                />
            )}
            <div ref={ref} />
            <PageHeader {...pageHeaderProps}>{children}</PageHeader>
        </>
    );
};

export default ResourceSinglePageHeader;
